import * as _ from "lodash";
import { FC, useEffect, ReactElement } from "react";
import { useSelector, useDispatch } from "react-redux";

import { FarmEntity, GamePlans, EnterpriseAppState, Enterprise, GamePlanList } from "@ctra/api";

import { Epoch, isDispatched, isPending } from "@ctra/utils";

export enum GamePlanListFilter {
  active = "active",
  previous = "previous"
}

interface GamePlanListProviderProps {
  farmID?: FarmEntity["id"];
  filter?: GamePlanListFilter;
  children: (gamePlanList: GamePlanList, meta: { isLoading: boolean }) => ReactElement | null;
}

/**
 * Get the game plans for the given farm or the whole app
 * @param children
 * @param filter
 * @param farmID - a special case, -1 indicates that all farms need to be fetched
 * @constructor
 */
export const GamePlanListProvider: FC<GamePlanListProviderProps> = ({ children, filter, farmID = -1 }) => {
  const dispatch = useDispatch();

  /**
   * Check how many game plans the user has
   */
  const gamePlanList = useSelector<EnterpriseAppState, GamePlanList>((state) => {
    const gamePlans = Enterprise.entities.getGamePlanList(state, farmID === -1 ? {} : { farmID });
    const collectionMethod = filter === GamePlanListFilter.active ? "pickBy" : "omitBy";

    return filter
      ? _[collectionMethod](gamePlans, ({ timePeriod: { endEpoch } }) =>
          Epoch.asMoment(endEpoch).isAfter(Epoch.asLong(Epoch.now()))
        )
      : gamePlans;
  });

  /**
   * Tell if the game plans have already been fetched.
   * This is handy because the game plan list can be simply empty, so
   * we don't want to keep on fetching.
   */
  const hasFetched = useSelector<EnterpriseAppState, boolean>(
    (state) =>
      isDispatched(state, GamePlans.types.FETCH_GAME_PLAN_LIST, { primaryValue: -1 }) ||
      isDispatched(state, GamePlans.types.FETCH_GAME_PLAN_LIST, { primaryValue: farmID })
  );

  /**
   * Tell if the game plan list is being loaded
   */
  const isLoading = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, GamePlans.types.FETCH_GAME_PLAN_LIST, { primaryValue: farmID })
  );

  useEffect(() => {
    if (!hasFetched) {
      dispatch(GamePlans.actions.fetchGamePlans.start(farmID));
    }
  }, [dispatch, farmID, hasFetched, gamePlanList]);

  return children(gamePlanList, { isLoading });
};
